import { useCallback, useMemo, useState, type FormEvent } from "react";

import { createOwnerApiClient, OwnerApiError, type OwnerApiClient } from "./api.js";
import type {
  AuditEvent,
  BudgetStatus,
  ControlResult,
  OwnerAction,
  RuntimeStatus
} from "./types.js";

const DEFAULT_ORGANIZATION = import.meta.env.VITE_ORGANIZATION_ID ?? "mock-organization";

export function App() {
  const client = useMemo(
    () =>
      createOwnerApiClient({
        mode: import.meta.env.VITE_CONSOLE_MODE,
        baseUrl: import.meta.env.VITE_CONTROL_API_URL
      }),
    []
  );
  const [token, setToken] = useState("");
  const [organizationId, setOrganizationId] = useState(DEFAULT_ORGANIZATION);
  const [status, setStatus] = useState<RuntimeStatus | null>(null);
  const [budget, setBudget] = useState<BudgetStatus | null>(null);
  const [events, setEvents] = useState<readonly AuditEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // allSettled (no Promise.all) para no ocultar cuál de los tres endpoints falla:
      // aplicamos lo que tenga éxito (permite éxito parcial → el badge pasa a SHADOW
      // aunque otro endpoint falle) y detallamos cada fallo con su endpoint/status.
      const [statusResult, budgetResult, auditResult] = await Promise.allSettled([
        client.getStatus(token),
        client.getBudget(organizationId, token),
        client.getAudit(organizationId, token)
      ]);

      if (statusResult.status === "fulfilled") setStatus(statusResult.value);
      if (budgetResult.status === "fulfilled") setBudget(budgetResult.value);
      if (auditResult.status === "fulfilled") setEvents(auditResult.value.events);

      const failures: string[] = [];
      if (statusResult.status === "rejected") failures.push(describeFailure("status", statusResult.reason));
      if (budgetResult.status === "rejected") failures.push(describeFailure("budget", budgetResult.reason));
      if (auditResult.status === "rejected") failures.push(describeFailure("audit", auditResult.reason));

      setError(failures.length > 0 ? failures.join(" · ") : null);
    } finally {
      setLoading(false);
    }
  }, [client, organizationId, token]);

  const control = useCallback(
    async (action: OwnerAction, reason: string): Promise<ControlResult | null> => {
      setLoading(true);
      setError(null);
      setNotice(null);
      try {
        const result = await client.control(action, reason, token);
        setNotice(result.message);
        setStatus(await client.getStatus(token));
        return result;
      } catch (caught) {
        setError(toMessage(caught));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [client, token]
  );

  return (
    <div className="app-shell">
      <header className="topbar">
        <div>
          <p className="eyebrow">OWNER CONTROL PLANE</p>
          <h1>Autonomous Company</h1>
        </div>
        <ModeBadge client={client} executionMode={status?.executionMode} />
      </header>

      {client.mode === "mock" && (
        <div className="banner banner-safe" role="status">
          Mock shadow session. All values and owner controls are simulated locally.
        </div>
      )}

      <section className="connection panel" aria-labelledby="connection-heading">
        <div>
          <p className="eyebrow" id="connection-heading">CONNECTION</p>
          <p className="muted">The owner token is held in memory only and disappears on reload.</p>
        </div>
        <label>
          Organization
          <input value={organizationId} onChange={(event) => setOrganizationId(event.target.value)} />
        </label>
        <label>
          Owner token
          <input
            type="password"
            value={token}
            autoComplete="off"
            spellCheck={false}
            placeholder={client.mode === "mock" ? "not required in mock" : "Bearer token"}
            onChange={(event) => setToken(event.target.value)}
          />
        </label>
        <button className="button primary" disabled={loading} onClick={() => void refresh()}>
          {loading ? "Refreshing…" : "Refresh evidence"}
        </button>
      </section>

      {error && <div className="banner banner-error" role="alert">{error}</div>}
      {notice && <div className="banner" role="status">{notice}</div>}

      <main>
        <section className="metric-grid" aria-label="Runtime status">
          <Metric label="Runtime" value={status?.state ?? "unknown"} accent={status?.state} />
          <Metric label="Active workflows" value={status?.activeWorkflows ?? "—"} />
          <Metric label="Pending actions" value={status?.pendingActions ?? "—"} />
          <Metric label="Open incidents" value={status?.openIncidents ?? "—"} />
        </section>

        <div className="content-grid">
          <BudgetPanel budget={budget} />
          <OwnerControls disabled={loading} onControl={control} />
        </div>

        <AuditPanel events={events} />
      </main>
    </div>
  );
}

function ModeBadge({
  client,
  executionMode
}: {
  client: OwnerApiClient;
  executionMode: RuntimeStatus["executionMode"] | undefined;
}) {
  const label = client.mode === "mock" ? "MOCK / NO NETWORK" : (executionMode ?? "UNVERIFIED").toUpperCase();
  return <span className={`mode-badge mode-${executionMode ?? client.mode}`}>{label}</span>;
}

function Metric({
  label,
  value,
  accent
}: {
  label: string;
  value: string | number;
  accent?: string | undefined;
}) {
  return (
    <article className="metric panel">
      <p>{label}</p>
      <strong className={accent ? `text-${accent}` : ""}>{String(value)}</strong>
    </article>
  );
}

function BudgetPanel({ budget }: { budget: BudgetStatus | null }) {
  const used = budget ? budget.spent + budget.committed : 0;
  const ratio = budget && budget.monthlyLimit > 0 ? Math.min(100, (used / budget.monthlyLimit) * 100) : 0;
  return (
    <section className="panel" aria-labelledby="budget-heading">
      <p className="eyebrow" id="budget-heading">MONTHLY BUDGET</p>
      {budget ? (
        <>
          <div className="budget-row">
            <strong>{formatMoney(budget.remaining, budget.currency)} remaining</strong>
            <span>{formatMoney(budget.monthlyLimit, budget.currency)} cap</span>
          </div>
          <div className="progress" aria-label={`${ratio.toFixed(0)} percent allocated`}>
            <span style={{ width: `${ratio}%` }} />
          </div>
          <dl className="details">
            <div><dt>Spent</dt><dd>{formatMoney(budget.spent, budget.currency)}</dd></div>
            <div><dt>Committed</dt><dd>{formatMoney(budget.committed, budget.currency)}</dd></div>
          </dl>
        </>
      ) : (
        <p className="muted">Refresh to load the signed budget ledger.</p>
      )}
    </section>
  );
}

function OwnerControls({
  disabled,
  onControl
}: {
  disabled: boolean;
  onControl: (action: OwnerAction, reason: string) => Promise<ControlResult | null>;
}) {
  const [reason, setReason] = useState("");
  const [killPhrase, setKillPhrase] = useState("");
  const submit = (action: OwnerAction) => async (event: FormEvent) => {
    event.preventDefault();
    if (reason.trim().length < 8) return;
    if (action === "kill" && killPhrase !== "KILL") return;
    await onControl(action, reason.trim());
    setKillPhrase("");
  };
  const validReason = reason.trim().length >= 8;

  return (
    <section className="panel danger-zone" aria-labelledby="controls-heading">
      <p className="eyebrow" id="controls-heading">OWNER CONTROLS</p>
      <label>
        Required audit reason
        <textarea
          rows={2}
          value={reason}
          minLength={8}
          placeholder="Explain why this intervention is required"
          onChange={(event) => setReason(event.target.value)}
        />
      </label>
      <div className="control-row">
        <form onSubmit={submit("pause")}><button disabled={disabled || !validReason}>Pause</button></form>
        <form onSubmit={submit("resume")}><button disabled={disabled || !validReason}>Resume</button></form>
      </div>
      <label>
        Type KILL to enable the kill switch
        <input value={killPhrase} onChange={(event) => setKillPhrase(event.target.value)} />
      </label>
      <form onSubmit={submit("kill")}>
        <button className="button destructive" disabled={disabled || !validReason || killPhrase !== "KILL"}>
          Activate kill switch
        </button>
      </form>
    </section>
  );
}

function AuditPanel({ events }: { events: readonly AuditEvent[] }) {
  return (
    <section className="panel audit" aria-labelledby="audit-heading">
      <div className="section-heading">
        <div>
          <p className="eyebrow" id="audit-heading">AUDIT TRAIL</p>
          <h2>Recent immutable events</h2>
        </div>
        <span>{events.length} shown</span>
      </div>
      {events.length === 0 ? (
        <p className="muted">Refresh to load audit evidence.</p>
      ) : (
        <div className="table-scroll">
          <table>
            <thead><tr><th>Seq</th><th>Time</th><th>Event</th><th>Outcome</th><th>Summary</th></tr></thead>
            <tbody>
              {events.map((event) => (
                <tr key={event.id}>
                  <td>{event.sequence}</td>
                  <td>{formatTime(event.occurredAt)}</td>
                  <td><code>{event.eventType}</code></td>
                  <td>{event.outcome ?? "—"}</td>
                  <td>{event.summary ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function formatMoney(value: number, currency: string) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency }).format(value);
}

function formatTime(value: string) {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString("en-GB");
}

function toMessage(error: unknown) {
  return error instanceof Error ? error.message : "Unexpected console error";
}

// Describe un fallo por endpoint mostrando endpoint + status (si existe) + causa/cuerpo.
// El OwnerApiError ya incorpora la ruta, el status y el cuerpo de error de la respuesta.
function describeFailure(endpoint: string, reason: unknown): string {
  if (reason instanceof OwnerApiError) {
    const status = reason.status > 0 ? ` [HTTP ${reason.status}]` : "";
    return `${endpoint}${status}: ${reason.message}`;
  }
  if (reason instanceof Error) return `${endpoint}: ${reason.message}`;
  return `${endpoint}: unexpected error`;
}
